import { createApp } from "vue";
import { createPinia } from 'pinia';
import App from "./App.vue";
import router from "./router";
// import store from "./store";
import Toast, { type PluginOptions, POSITION } from "vue-toastification";
import 'hammerjs';
import { createI18n } from 'vue-i18n';

import "@/assets/css/App.scss";
import "@/assets/css/tailwind.css";
import "@/assets/css/finmony-theme.css";
// import "@popperjs/core";
// import "bootstrap";

import "@/assets/css/site.css";
import "@finmony/ui/styles";
import "@finmony/ui/dist/style.css";
import { FIcon, FLabel, FTitle } from "@finmony/ui";

import likelySubtags from "cldr-core/supplemental/likelySubtags.json";
import currencyData from "cldr-core/supplemental/currencyData.json";
import weekData from "cldr-core/supplemental/weekData.json";

import localCurrency from "cldr-numbers-full/main/tr/currencies.json";
import numbers from "cldr-numbers-full/main/tr/numbers.json";
import caGregorian from "cldr-dates-full/main/tr/ca-gregorian.json";
import dateFields from "cldr-dates-full/main/tr/dateFields.json";
import tr from "@/locales/tr.json";
import en from "@/locales/en.json";
import "@fontsource/roboto"
import { useErrorStore } from '@/stores/error';
import type { ErrorInfo } from '@/types/ErrorModel';
import { defaultLocale, localeOptions } from '@/constants/config';
import 'shepherd.js/dist/css/shepherd.css';
import 'driver.js/dist/driver.css';
import halicFaviconUrl from "@/assets/img/logo_halic.png";
import { getTenantRootDomain, isCustomDomain, setCustomDomainContext } from "@/utils/tenant";
import { bootstrapCustomDomain, renderCustomDomainUnavailable } from "@/utils/customDomainHost.mjs";

const app = createApp(App);
const finmonyGlobals = { FIcon, FLabel, FTitle };
Object.entries(finmonyGlobals).forEach(([name, component]) => {
  if (!app.component(name)) {
    app.component(name, component);
  }
});
const pinia = createPinia();

// Router, özel alan adı bootstrap'ı tamamlanmadan kurulmaz (kurulum ilk yönlendirmeyi başlatır).
app.use(pinia);

const messages = {
  en,
  tr,
};

const savedLocale = (localStorage.getItem('orionLanguage') && localeOptions.filter(x => x.id === localStorage.getItem('orionLanguage')).length > 0) ? localStorage.getItem('orionLanguage') : defaultLocale;

const i18n = createI18n({
  locale: savedLocale ?? 'tr',
  fallbackLocale: 'tr',
  messages,
});

app.use(i18n);

const options: PluginOptions = {
  position: POSITION.BOTTOM_RIGHT,
  timeout: 3500,
  closeOnClick: false,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: "button",
  icon: true,
  rtl: false,
  //transition: "Vue-Toastification__fade",
  maxToasts: 20,
  newestOnTop: true,
};
app.use(Toast, options);

app.config.errorHandler = (err, vm, info) => {
  const er = err as TypeError;
  const errorStore = useErrorStore();
  const errorInfo: ErrorInfo = {
    ErrorType: 'Error',
    ErrorText: er.message
  };
  console.log(errorInfo);
  errorStore.setError(errorInfo);
};

const startApp = () => {
  app.use(router);
  app.mount("#app");
};

if (isCustomDomain()) {
  // Müşterinin kendi alan adı: API aynı origin /api altında. Firma bu host için çözülemezse
  // uygulama monte edilmez, kontrollü bir hata ekranı gösterilir.
  void bootstrapCustomDomain({
    hostname: window.location.hostname,
    origin: window.location.origin,
    rootDomain: getTenantRootDomain(),
  }).then((result) => {
    if (result.status !== "ready") {
      renderCustomDomainUnavailable(document.getElementById("app"));
      return;
    }

    setCustomDomainContext({
      tenantSlug: result.tenantSlug,
      companyId: result.companyId,
      companyName: result.companyName,
      logoUrl: result.logoUrl,
      isGuestPaymentAllowed: result.isGuestPaymentAllowed,
      loginBranding: result.loginBranding,
    });
    if (result.companyName) document.title = `${result.companyName} - Tahsilat`;
    startApp();
  });
} else {
  startApp();
}

(() => {
  const host = window.location.host.toLowerCase();

  const setFavicon = (href: string) => {
    const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const linkEl = existing ?? document.createElement('link');
    linkEl.rel = 'icon';

    if (href.endsWith('.png')) linkEl.type = 'image/png';
    else if (href.endsWith('.svg')) linkEl.type = 'image/svg+xml';
    else linkEl.type = 'image/x-icon';
    linkEl.href = href;
    if (!existing) document.head.appendChild(linkEl);
  };

  if (host.includes('halicpayment')) {
    document.title = 'Haliç Üniversitesi Ödeme Platformu';
    setFavicon(halicFaviconUrl);
  } else if (host.includes('localhost')) {
    document.title = 'Finmony e-Kolay Tahsilat';
    setFavicon('/favicon.ico');
  } else {
    document.title = 'Finmony e-Kolay Tahsilat';
    setFavicon('/favicon.ico');
  }
})();
